/**
 * Amount owed — the creditors equivalent of the Assets panel's valuations list.
 *
 * Purpose: a creditor can have several competing views of what they are owed —
 * what the company's own records say, what the creditor claimed on their Proof
 * of Debt, and what was admitted on adjudication. This section lists them all as
 * cards, lets the practitioner endorse one as the figure of record, and lets
 * them add their own.
 *
 * The POD cards are derived from the claim rather than stored on the creditor,
 * so they follow the adjudication and can be endorsed but not edited here. The
 * endorsed figure is mirrored onto `liabilityEntries.amount` by the backend, so
 * everything downstream (the creditors table, secured netting, the outcome
 * waterfall) keeps reading a single number.
 *
 * @component
 * @example
 * <OwedEstimatesSection entry={entry} isViewer={isViewer} />
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useConvexMutation, convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Check, Pencil, Plus, Trash2, Undo2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { cn } from "@/utils/misc";
import { compareOwedEstimatesForDisplay } from "../shared-panels/owedEstimateOrder";
import type {
  CreditorOwedEstimateUI,
  CreditorOwedSourceUI,
  LiabilityEntryUI,
} from "./types";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const SOURCE_LABEL: Record<CreditorOwedSourceUI, string> = {
  document: "Company records",
  proof_of_debt: "Claimed on proof of debt",
  pod_admitted: "Admitted on adjudication",
  manual: "Practitioner estimate",
};

/** Single-character provenance badge, mirroring the asset valuation cards. */
const SOURCE_INITIAL: Record<CreditorOwedSourceUI, string> = {
  document: "D",
  proof_of_debt: "P",
  pod_admitted: "A",
  manual: "M",
};

const SOURCE_BADGE_CLASS: Record<CreditorOwedSourceUI, string> = {
  document: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
  proof_of_debt:
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  pod_admitted:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  manual:
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400",
};

/** Parses a typed figure, tolerating pasted values like "$1,200,000". */
function parseAmount(raw: string): number | null {
  const cleaned = raw.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// Estimate card
// ---------------------------------------------------------------------------

function EstimateCard({
  estimate,
  isSelected,
  canEdit,
  onSelect,
  onUpdateAmount,
  onUpdateAsAt,
  onDelete,
}: {
  estimate: CreditorOwedEstimateUI;
  isSelected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdateAmount: (amount: number) => void;
  /** An empty string clears the date. */
  onUpdateAsAt: (isoDate: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [draft, setDraft] = useState(String(estimate.amount));

  // A POD figure belongs to the claim, so it is endorsed from here but only
  // changed by re-adjudicating.
  const canAmend = canEdit && !estimate.isLive;
  const label = estimate.label ?? SOURCE_LABEL[estimate.source];
  const asAt = fmtDate(estimate.asAtDate);

  function commit() {
    const parsed = parseAmount(draft);
    if (parsed != null && parsed !== estimate.amount) onUpdateAmount(parsed);
    setEditing(false);
  }

  return (
    <div
      onClick={canEdit && !editing ? onSelect : undefined}
      className={cn(
        "group rounded-lg border p-3 transition-colors",
        isSelected
          ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20"
          : "border-border hover:border-muted-foreground/30",
        canEdit && "cursor-pointer",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold",
              SOURCE_BADGE_CLASS[estimate.source],
            )}
          >
            {SOURCE_INITIAL[estimate.source]}
          </div>
          <span className="truncate text-xs font-semibold text-foreground">
            {label}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {asAt ? (
            <span
              className={cn(
                "whitespace-nowrap text-[10px] text-muted-foreground",
                canAmend && "cursor-text hover:text-foreground",
              )}
              title={canAmend ? "Change the as-at date" : undefined}
              onClick={(e) => {
                if (!canAmend) return;
                e.stopPropagation();
                setEditingDate(true);
              }}
            >
              {asAt}
            </span>
          ) : (
            canAmend && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingDate(true);
                }}
                className="whitespace-nowrap text-[10px] text-muted-foreground/60 hover:text-foreground"
              >
                Set date
              </button>
            )
          )}
          {canAmend && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
              title="Remove this estimate"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
          {isSelected && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-2">
        {editing && canAmend ? (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setDraft(String(estimate.amount));
                  setEditing(false);
                }
              }}
              className="h-7 w-36 text-sm font-semibold tabular-nums"
              autoFocus
            />
            <Button size="sm" className="h-7 text-xs" onClick={commit}>
              Save
            </Button>
          </div>
        ) : (
          <span
            className={cn(
              "text-sm font-bold tabular-nums",
              isSelected
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-foreground",
              canAmend && "cursor-text",
            )}
            onClick={(e) => {
              if (!canAmend) return;
              e.stopPropagation();
              setDraft(String(estimate.amount));
              setEditing(true);
            }}
          >
            {AUD.format(estimate.amount)}
            {canAmend && (
              <Pencil className="ml-1 inline-block h-2.5 w-2.5 opacity-30" />
            )}
          </span>
        )}
      </div>

      {editingDate && canAmend && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={estimate.asAtDate ?? ""}
            onChange={(iso) => {
              onUpdateAsAt(iso);
              setEditingDate(false);
            }}
            placeholder="As at"
            className="w-44"
            inputClassName="h-8 pl-7 pr-7 text-xs tabular-nums border-input rounded-md bg-background"
          />
        </div>
      )}

      {estimate.sourceDocument && (
        <p className="mt-2 truncate text-[10px] text-muted-foreground">
          {estimate.sourceDocument.fileName}
          {estimate.sourceDocument.section &&
            ` \u00B7 ${estimate.sourceDocument.section}`}
        </p>
      )}
      {estimate.isLive && (
        <p className="mt-2 text-[10px] text-muted-foreground">
          From the proof of debt — edit the claim to change it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add form
// ---------------------------------------------------------------------------

function AddEstimateForm({
  onAdd,
  onCancel,
}: {
  onAdd: (input: { amount: number; asAtDate?: string; notes?: string }) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [asAt, setAsAt] = useState("");
  const [notes, setNotes] = useState("");

  const parsed = parseAmount(amount);

  return (
    <div className="space-y-3 rounded-lg border-2 border-dashed border-violet-200 p-4 dark:border-violet-800">
      <p className="text-sm font-semibold text-violet-700 dark:text-violet-400">
        Add practitioner estimate
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Amount ($)
        </label>
        <Input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          className="h-9 text-sm tabular-nums"
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          As at
        </label>
        <DatePicker value={asAt} onChange={setAsAt} className="w-full" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notes
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Basis for this estimate..."
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={parsed == null}
          onClick={() => {
            if (parsed == null) return;
            onAdd({
              amount: parsed,
              asAtDate: asAt.trim() || undefined,
              notes: notes.trim() || undefined,
            });
          }}
        >
          Add &amp; select
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

/**
 * Every card the creditor holds: the figures stored on the row plus the ones
 * derived live from a linked proof of debt. Shared by the banner and the list
 * below it — TanStack Query dedupes the two calls into one subscription.
 */
function useOwedEstimates(entryId: Id<"liabilityEntries">) {
  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.liabilities.publicQueries
        .listCreditorOwedEstimates,
      { entryId },
    ),
  );
  return data ?? [];
}

/**
 * The headline figure, mirroring the Selected Value banner on an asset: the
 * number every other view of this job reads, and where it came from.
 *
 * Rendered by the panel outside its padded body so the band runs full width,
 * so it lives here beside the list it summarises rather than in the panel.
 *
 * @component
 * @example
 * <OwedAmountBanner entry={entry} isViewer={isViewer} />
 */
export function OwedAmountBanner({
  entry,
  isViewer,
}: {
  entry: LiabilityEntryUI;
  isViewer?: boolean;
}) {
  const entryId = entry._id as Id<"liabilityEntries">;
  const cards = useOwedEstimates(entryId);
  const selectedId = entry.selectedOwedEstimate?.estimateId;
  const selected = cards.find((e) => e.id === selectedId);

  const clearSelection = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .clearCreditorOwedSelection,
    ),
  });

  const asAt = fmtDate(selected?.asAtDate ?? entry.date);

  return (
    <div className="border-y border-emerald-200/30 bg-emerald-50/50 px-5 py-3 dark:border-emerald-800/30 dark:bg-emerald-950/20">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
            Est Owed
          </p>
          <span className="block text-2xl font-bold tabular-nums text-foreground">
            {AUD.format(entry.amount)}
          </span>
          <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
            {selected
              ? `via ${selected.label ?? SOURCE_LABEL[selected.source]}`
              : cards.length > 1
                ? "Most authoritative figure on file \u00B7 not yet confirmed"
                : "The only figure on file \u00B7 not yet confirmed"}
            {asAt && ` \u00B7 as at ${asAt}`}
          </p>
        </div>
        {!isViewer && selected && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={() => clearSelection.mutate({ entryId })}
            title="Stop pinning the figure and let it follow whichever estimate is most authoritative"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Unpin
          </Button>
        )}
      </div>
    </div>
  );
}

export function OwedEstimatesSection({
  entry,
  isViewer,
}: {
  entry: LiabilityEntryUI;
  isViewer?: boolean;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const canEdit = !isViewer;
  const entryId = entry._id as Id<"liabilityEntries">;

  const cards = useOwedEstimates(entryId);
  // Show the creditor's own figures (Proof of Debt / emailed claim + its
  // adjudicated amount) above the company's books and records.
  const orderedCards = [...cards].sort(compareOwedEstimatesForDisplay);
  const selectedId = entry.selectedOwedEstimate?.estimateId;

  const addEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .addCreditorOwedEstimate,
    ),
  });
  const updateEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .updateCreditorOwedEstimate,
    ),
  });
  const deleteEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .deleteCreditorOwedEstimate,
    ),
  });
  const selectEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .selectCreditorOwedEstimate,
    ),
  });
  const clearSelection = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .clearCreditorOwedSelection,
    ),
  });

  return (
    <>
      <div className="flex items-center justify-between pb-1.5 pt-6">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-widest text-[#121212] dark:text-foreground">
            Estimates
          </span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
            {cards.length}
          </span>
        </div>
        {canEdit && !showAddForm && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 border-violet-200 text-xs text-violet-600 dark:border-violet-800 dark:text-violet-400"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-3 w-3" />
            Add estimate
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {orderedCards.map((estimate) => (
          <EstimateCard
            key={estimate.id}
            estimate={estimate}
            isSelected={estimate.id === selectedId}
            canEdit={canEdit}
            onSelect={() => {
              // Clicking the endorsed card again lets the figure float back to
              // whatever is most authoritative.
              if (estimate.id === selectedId) {
                clearSelection.mutate({ entryId });
              } else {
                selectEstimate.mutate({ entryId, estimateId: estimate.id });
              }
            }}
            onUpdateAmount={(amount) =>
              updateEstimate.mutate({
                entryId,
                estimateId: estimate.id,
                amount,
              })
            }
            onUpdateAsAt={(isoDate) =>
              updateEstimate.mutate({
                entryId,
                estimateId: estimate.id,
                ...(isoDate ? { asAtDate: isoDate } : { clearAsAtDate: true }),
              })
            }
            onDelete={() =>
              deleteEstimate.mutate({ entryId, estimateId: estimate.id })
            }
          />
        ))}

        {cards.length === 0 && !showAddForm && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No figures yet.
          </p>
        )}

        {showAddForm && (
          <AddEstimateForm
            onAdd={(input) => {
              addEstimate.mutate({ entryId, ...input });
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    </>
  );
}
